import React, { useState, useEffect, useRef } from 'react'
import { Dialog, Box, Typography, LinearProgress } from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import ReasonForVisitModal from './StepperComponents/ReasonForVisitModal'
import IntakeNewModal from './StepperComponents/IntakeForm/IntakeNewModal'
import IntakeTherapyModal from './StepperComponents/IntakeForm/IntakeTherapyModal'
import VerifyInformationStep from './StepperComponents/VerifyInformationStep'
import { InsuranceModal } from './StepperComponents/InsuranceModal'
import CheckboxOption from '../../ClinicalNotes/reusable/CheckboxOptions'

const BEHAVIORAL_HEALTH = ['PSYINT', 'PSYCHILDINT', 'THERAPYINT']

const font = (size, weight, color) => ({
    fontFamily: 'Inter, sans-serif',
    fontSize: size,
    fontWeight: weight,
    color,
})




function buildSteps(visitType, selfpay, hasInsurance) {
    const type = visitType?.id?.split(',')[0]
    const isBH = BEHAVIORAL_HEALTH.includes(type)
    const isTherapy = type === 'THERAPYINT'
    
    if (isTherapy) {
        return [
            { key: 'reason',       label: 'Reason for Visit'    },
            { key: 'demographics', label: 'Verify Information'  },
            { key: 'intake',       label: 'Associate Consent', isTherapy: true },
        ]
    }

    return [
        { key: 'reason',       label: 'Reason for Visit'    },
        ...(isBH ? [{ key: 'intake', label: 'Intake Forms', isTherapy: false }] : []),
        { key: 'demographics', label: 'Verify Information' },
    ]
}

export default function AppointmentPostBookingModal({
    open,
    onClose,
    onComplete,
    patientData = {},
    visitType,
    // DemoGraphModal
    isMobileView,
    selctedProviderDetails,
    onBackToProvider,
    // InsuranceModal
    carrierList = [],
    insuranceData = {},
    onUpdateInsurance,
    // IntakeNewModal
    intakeFormValues,
    intakeSetValues,
    // IntakeTherapyModal
    checkboxTherapyConsent,
    setCheckboxTherapyConsent,
    therapyConsentStep,
    setTherapyConsentStep,
    selfpay = false,
}) {
    const [currentStep, setCurrentStep] = useState(0)
    const [reasonText,  setReasonText]  = useState('')
    const [verifyMode, setVerifyMode] = useState('summary')
    const [insuranceEditValid, setInsuranceEditValid] = useState(false)
    const insuranceRef = useRef(null)
    const demographicsRef = useRef(null)
    const hasInsurance = !!(insuranceData?.insurance_Id || insuranceData?.carrier)


    const steps = buildSteps(visitType, selfpay,hasInsurance)
    const total = steps.length
    const progress = total > 1 ? (currentStep / (total - 1)) * 100 : 100

    useEffect(() => {
        if (open) {
            setCurrentStep(0)
            setReasonText('')
            setVerifyMode('summary')
            setInsuranceEditValid(false)
        }
    }, [open])

    const step = steps[currentStep]

    const isReasonStep = step?.key === 'reason'
    const isVerifyStep = step?.key === 'demographics'
    const isConsentStep = step?.key === 'intake' && step?.isTherapy
    const isRegularIntakeStep = step?.key === 'intake' && !step?.isTherapy

    const getPrimaryButtonText = () => {
        if (isVerifyStep) return 'Yes, this information is correct'
        if (isConsentStep) return 'Complete'
        if (isReasonStep || isRegularIntakeStep) return 'Continue'
        return 'Continue'
    }

    const handlePrimaryAction = () => {
    if (isVerifyStep) {
        demographicsRef.current?.submit()
        return
    }

        advance()
    }

    const subscriberMissing = hasInsurance && !(insuranceData?.subscriber_id || insuranceData?.subscriberNum)

    const isPrimaryDisabled = () => {
        if (isConsentStep) return !checkboxTherapyConsent
        if (isVerifyStep && verifyMode === 'summary' && subscriberMissing) return true
        if (isVerifyStep && verifyMode === 'editInsurance' && !insuranceEditValid) return true
        return !canContinue()
    }

    const intakeCanContinue =
        !!intakeFormValues?.values?.minor &&
        !!intakeFormValues?.values?.careOrAssessment &&
        !!intakeFormValues?.values?.harmingYourself &&
        !!intakeFormValues?.values?.harmingOthers &&
        !!intakeFormValues?.values?.alcoholAbuse &&
        !!intakeFormValues?.values?.substanceAbuse

    const canContinue = () => {
        if (!step) return false
        if (step.key === 'reason') return reasonText.trim().length > 0
        if (step.key === 'intake') {
            if (step.isTherapy) return therapyConsentStep === 2 ? !!checkboxTherapyConsent : true
            return intakeCanContinue
        }
        // demographics & insurance — their own Save/Continue buttons handle progression
        return true
    }

    const advance = () => {
        if (currentStep < total - 1) setCurrentStep(s => s + 1)
        else onComplete?.({ reasonText })
    }

    const handleBack = () => {
        if (currentStep === 0) onClose?.()
        else setCurrentStep(s => s - 1)
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '980px',
                    maxWidth: '96vw',
                    height: '640px',
                    maxHeight: '90vh',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    margin: '16px',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <Box sx={{display: 'flex',flex: 1,minHeight: 0,overflow: 'hidden',}}>
                <Box
                    sx={{
                        width: '260px',
                        minWidth: '260px',
                        background: '#F4F6FB',
                        borderRight: '1px solid #E4E8F0',
                        padding: '24px 20px',
                        display: { xs: 'none', sm: 'flex' },
                        flexDirection: 'column',
                    }}
                    >

                    <Typography
                        style={{
                        ...font('16px', 600, '#1A2340'),
                        marginBottom: '6px'
                        }}
                    >
                        Complete your appointment details
                    </Typography>

                    <Typography
                        style={{
                        ...font('13px', 500, '#5B6B8B'),
                        marginBottom: '10px'
                        }}
                    >
                        Step {currentStep + 1} of {total}: <strong>{step?.label}</strong>
                    </Typography>

                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                        height: '6px',
                        borderRadius: '6px',
                        backgroundColor: '#DDE3F4',
                        marginBottom: '20px',
                        '& .MuiLinearProgress-bar': {
                            backgroundColor: '#5571C6',
                            borderRadius: '6px',
                        },
                        }}
                    />

                    <Typography
                        style={{
                        ...font('12px', 600, '#1A2340'),
                        marginBottom: '10px',
                        }}
                    >
                        Your Progress
                    </Typography>

                    {steps.map((s, i) => {
                        const isCurrent = i === currentStep
                        const isCompleted = i < currentStep
                        const isIncomplete = i > currentStep

                        return (
                            <Box
                                key={s.key}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    padding: '10px 12px',
                                    borderRadius: '8px',
                                    marginBottom: '6px',
                                    background: isCurrent
                                    ? '#5C73B7' 
                                    : isCompleted
                                    ? '#ffffff'
                                    : '#EEF1F6',
                                    boxShadow: isCurrent
                                    ? '0 4px 12px rgba(0,0,0,0.2)'
                                    : 'none',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {/* ICON STATES */}

                                {isCompleted && (
                                    <CheckCircleIcon
                                        sx={{
                                            fontSize: '20px',
                                            color: '#4CAF50'
                                        }}
                                    />
                                )}

                                {isCurrent && (
                                    <Box
                                        sx={{
                                        width: '22px',
                                        height: '22px',
                                        borderRadius: '50%',
                                        border: '2px solid #fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '3px'
                                        }}
                                    >
                                        <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', background: '#fff' }} />
                                        <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', background: '#fff' }} />
                                        <Box sx={{ width: '4px', height: '4px', borderRadius: '50%', background: '#fff' }} />
                                    </Box>
                                )}

                                {isIncomplete && (
                                    <Box
                                        sx={{
                                            width: '18px',
                                            height: '18px',
                                            borderRadius: '50%',
                                            border: '2px solid #C7D0EA',
                                            background: '#E6ECFA'
                                        }}
                                    />
                                )}

                                {/* LABEL */}

                                <Typography
                                    style={{
                                        ...font(
                                        '13px',
                                        500,
                                        isCurrent ? '#fff' : '#000'
                                        )
                                    }}
                                >
                                {s.label}
                                </Typography>
                            </Box>
                        )
                    })}

                    <Typography style={{...font('12px', 400, '#8892A4'), marginTop: 'auto',paddingTop: '16px',}}>
                        Takes about 5 minutes total
                    </Typography>

                </Box>

                <Box sx={{flex: 1,display: 'flex',flexDirection: 'column', background: '#fff', minHeight: 0,overflow: 'hidden',}}>
                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >

                        {step?.key === 'reason' && (
                            <Box sx={{ width: '100%', overflowX: 'hidden' }}>
                                <ReasonForVisitModal
                                    reasonForVisitText={reasonText}
                                    setReasonForVisitText={setReasonText}
                                    onClose={onClose}
                                />
                            </Box>
                        )}

                        {step?.key === 'intake' && !step.isTherapy && (
                            <IntakeNewModal
                                setOpenIntakeModal={() => setCurrentStep(s => s - 1)}
                                intakeValues={intakeFormValues}
                                intakeSetValues={intakeSetValues}
                                setValue={() => {}}
                                isMobileView={isMobileView}
                            />
                        )}

                        {step?.key === 'intake' && step.isTherapy && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden',
                                }}
                            >
                                <Box
                                    sx={{
                                        flex: 1,
                                        minHeight: 0,
                                        overflow: 'hidden',
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <IntakeTherapyModal
                                        setOpenIntakeTherapyModal={() => setCurrentStep(s => s - 1)}
                                        therapyConsentStep={therapyConsentStep}
                                        setTherapyConsentStep={setTherapyConsentStep}
                                        setValue={() => {}}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        padding: '8px 23px 12px 23px',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        gap: '10px',
                                        flexShrink: 0,
                                        background: '#fff',
                                    }}
                                >
                                    <CheckboxOption
                                        name="therapyConsent"
                                        option={{ label: '', value: true }}
                                        checked={checkboxTherapyConsent}
                                        onChange={() => setCheckboxTherapyConsent(v => !v)}
                                        disabled={false}
                                        defaultChecked={false}
                                        classRoot=""
                                    />
                                    <Typography style={{ ...font('13px', 400, '#1a2340'), marginTop: '2px' }}>
                                        By signing below, I acknowledge that I have read and understood this consent form and 
                                        agree to receive therapy services from an Associate Therapist under supervision. 
                                        I agree that my electronic signature on this application is binding and enforceable, 
                                        as if I had signed a paper copy.
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {step?.key === 'demographics' && (
                            <VerifyInformationStep
                                ref={demographicsRef}
                                patientData={patientData}
                                insuranceData={insuranceData}
                                carrierList={carrierList}
                                onUpdateInsurance={onUpdateInsurance}
                                isMobileView={isMobileView}
                                selctedProviderDetails={selctedProviderDetails}
                                onBackToProvider={onBackToProvider}
                                handleNextStep={advance}
                                selfpay={selfpay}
                                onModeChange={setVerifyMode}
                                onInsuranceValidityChange={setInsuranceEditValid}
                            />
                        )}
                    </Box>

                    
                </Box>
            </Box>

            
            {(step?.key === 'reason' ||
                step?.key === 'intake' ||
                step?.key === 'demographics' ||
                step?.key === 'insurance') && (
                    <Box
                        sx={{
                            height: '72px',
                            borderTop: '1px solid #E8ECF6',
                            background: '#FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: '12px',
                            px: 3,
                            flexShrink: 0,
                        }}
                    >
                        <button
                            onClick={handleBack}
                            style={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '14px',
                                fontWeight: 500,
                                color: '#5571C6',
                                background: '#fff',
                                border: '1.5px solid #5571C6',
                                borderRadius: '8px',
                                padding: '9px 28px',
                                cursor: 'pointer',
                                minWidth: '112px',
                                height: '44px',
                            }}
                        >
                            Back
                        </button>

                        <button
                            onClick={handlePrimaryAction}
                            disabled={isPrimaryDisabled()}
                            style={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '14px',
                                fontWeight: 600,
                                color: '#fff',
                                background: isPrimaryDisabled() ? '#B8C4E8' : '#5571C6',
                                border: 'none',
                                borderRadius: '8px',
                                padding: '9px 28px',
                                cursor: isPrimaryDisabled() ? 'not-allowed' : 'pointer',
                                minWidth: isVerifyStep ? '220px' : '112px',
                                height: '44px',
                            }}
                        >
                            {getPrimaryButtonText()}
                        </button>
                    </Box>
            )}
        </Dialog>
    )
}
